import React, { useEffect } from "react";
import { cn } from "lib/utils";
import { useConfig } from "hooks/use-config";
import { useMenuHoverConfig } from "hooks/use-menu-hover";

const SidebarContent = ({ children }: { children: React.ReactNode }) => {
  const [config, setConfig] = useConfig();
  const [hoverConfig, setHoverConfig] = useMenuHoverConfig();
  const sidebarTheme =
    config.sidebarTheme !== "light"
      ? `dark theme-${config.sidebarTheme}`
      : `theme-${config.sidebarTheme}`;
  // On small screens the sidebar is a drawer; `menuHidden` is its closed state.
  const drawerOpen = !config.menuHidden;
  const closeDrawer = () => setConfig({ ...config, menuHidden: true });

  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setConfig((current) => ({ ...current, menuHidden: true }));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawerOpen, setConfig]);

  if (config.layout === "horizontal") return null;

  if (config.sidebar === "two-column") {
    return (
      <aside
        className={cn(
          "fixed  z-[45]    h-full  xl:flex hidden",
          sidebarTheme,
          {},
        )}
      >
        <div className=" relative  flex   h-full  ">{children}</div>
      </aside>
    );
  }

  return (
    <>
      {/* Backdrop behind the drawer on small screens; tapping it closes the menu. */}
      <div
        aria-hidden="true"
        onClick={closeDrawer}
        className={cn(
          "fixed inset-0 z-[44] bg-default-950/30 backdrop-blur-[2px] transition-opacity duration-300 lg:hidden",
          drawerOpen ? "opacity-100" : "pointer-events-none opacity-0",
        )}
      />
      <aside
        onMouseEnter={() =>
          config.sidebar === "classic" && setHoverConfig({ hovered: true })
        }
        onMouseLeave={() =>
          config.sidebar === "classic" && setHoverConfig({ hovered: false })
        }
        className={cn(
          "fixed inset-y-0 start-0 z-[45] flex w-[280px] max-w-[85vw] flex-col border-e bg-sidebar transition-[transform,width] duration-300 ease-out lg:w-[248px] lg:translate-x-0",
          sidebarTheme,
          drawerOpen ? "translate-x-0 shadow-deep lg:shadow-none" : "-translate-x-full",
          {
            "lg:w-[72px]": config.collapsed && !hoverConfig.hovered,
          },
        )}
      >
        {children}
      </aside>
    </>
  );
};

export default SidebarContent;
